import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';

import { TitleCasePipe } from '@angular/common';
import { CustomerStoreService } from '@app/ngrx-customer/services/customer';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { LastBuy } from '@product-detail/models/last-buy.model';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { dateIsLessThan, formatDate } from '@product-detail/utils/date.util';
import { formatLast4BuysList } from '@product-detail/utils/product-last-buys.util';
import { RecentPurchase } from '@usf/list-types';
import { SellerPriceDetails } from '@usf/price-types';
import * as dayjs from 'dayjs';
import { take } from 'rxjs/operators';
import { FormatCurrencyAndDecimalPipe } from 'src/app/shared/pipes/format-currency-and-decimal.pipe';

@Component({
  selector: 'app-seller-detail-more-information',
  templateUrl: './seller-detail-more-information.component.html',
  styleUrls: ['./seller-detail-more-information.component.scss'],
  providers: [FormatCurrencyAndDecimalPipe, TitleCasePipe],
  encapsulation: ViewEncapsulation.None,
})
export class SellerDetailMoreInformationComponent implements OnChanges {
  @Input() currentProductNumber: number;
  @Input() product: ProductDetailViewModel;
  @Input() sellerPriceDetails: SellerPriceDetails;
  @Input() isDynamicLastFourBuysLookbackEnabled: boolean;

  last4ProductsDataSource: LastBuy[];

  purchaseByCustomerDisplayColumns: string[] = [
    'customer',
    'divisionName',
    'quantity',
    'inventoryDate',
    'cost',
  ];
  purchaseByCustomerProductsDataSource: {
    customer: string;
    divisionName: string;
    quantity: string;
    inventoryDate: string;
    cost: string;
  }[] = [];
  purchaseByCustomerProductsHeaderConfig = [
    { title: 'Customer', cellReference: 'customer' },
    { title: 'Division', cellReference: 'divisionName' },
    { title: 'Qty', cellReference: 'quantity' },
    { title: 'Order Date', cellReference: 'inventoryDate' },
    { title: 'Cost', cellReference: 'cost' },
  ];

  constructor(
    readonly platformService: PlatformService,
    protected appState: PanAppState,
    private currency: FormatCurrencyAndDecimalPipe,
    private customerStoreService: CustomerStoreService,
    private titleCasePipe: TitleCasePipe,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!!changes.sellerPriceDetails?.currentValue?.lastFourBuys?.lastbuyList) {
      this.formatLast4Buys();
    }
    if (changes.product?.currentValue?.recentPurchaseByCustomers) {
      this.formatPurchaseByCustomer();
    }
  }

  formatLast4Buys() {
    this.last4ProductsDataSource = formatLast4BuysList(
      this.sellerPriceDetails?.lastFourBuys?.lastbuyList,
      this.product?.summary,
      this.isDynamicLastFourBuysLookbackEnabled,
    );
  }

  formatPurchaseByCustomer() {
    const recentPurchaseByCustomers = this.product?.recentPurchaseByCustomers;
    this.purchaseByCustomerProductsDataSource = [];
    if (recentPurchaseByCustomers?.length >= 0) {
      recentPurchaseByCustomers.forEach(recentPurchase => {
        const items = Object.keys(recentPurchase).filter(
          b => b.search('purchasePrice') === 0,
        );
        items.forEach((item, index) => {
          const inventoryDate = recentPurchase[`purchaseDtm${index + 1}`];
          const purchaseFormatted = this.formatProductsPurchaseByCustomer(
            items,
            recentPurchase,
            inventoryDate,
            index + 1,
          );
          if (purchaseFormatted) {
            this.purchaseByCustomerProductsDataSource.push(purchaseFormatted);
          }
        });
      });
      this.purchaseByCustomerProductsDataSource.sort((a, b) =>
        dayjs(b.inventoryDate).diff(dayjs(a.inventoryDate)),
      );
    }
  }

  formatProductsPurchaseByCustomer(
    allItems: string[],
    buys: RecentPurchase,
    date: dayjs.Dayjs,
    order: number,
  ) {
    let customerName = '',
      divisionName = '';
    if (!(allItems.length >= 4 && dateIsLessThan(180, 'days', dayjs(date)))) {
      return null;
    }
    this.customerStoreService
      .loadCustomerWithNumber$(buys.customerNumber)
      .pipe(take(1))
      .subscribe(customer => (customerName = customer.customerName));

    this.customerStoreService
      .loadDivisionByDivisionNumber$(buys.divisionNumber)
      .pipe(take(1))
      .subscribe(division => (divisionName = division.divisionName));

    const quantity = `${buys[`unitsOrdered${order}`]} ${
      this.product?.summary?.priceUom
    }`;

    const quantityCell = buys[`eachesOrdered${order}`]
      ? `${quantity} | ${buys[`eachesOrdered${order}`]} ${
          this.product?.summary?.catchWeightFlag &&
          this.product?.summary?.breakable
            ? this.product?.summary.priceUom
            : this.product?.summary?.eachUom
        }`
      : quantity;
    return {
      customer: this.titleCasePipe.transform(customerName),
      divisionName: this.titleCasePipe.transform(divisionName),
      quantity: quantityCell,
      inventoryDate: formatDate('MM/DD/YY', dayjs(date)),
      cost: `${this.currency.transform(buys[`purchasePrice${order}`])} ${
        this.product?.summary?.priceUom
      }`,
    };
  }
}
